import { useState, useEffect, useMemo } from 'react';
import { API, formatDateTime } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Clock, Shield, Package, CreditCard, Store, ChevronDown, ChevronUp } from 'lucide-react';

const roleConfig = {
  all: { label: 'All', icon: Bell, color: 'text-foreground', bg: 'bg-muted' },
  admin: { label: 'Admin', icon: Shield, color: 'text-purple-400', bg: 'bg-purple-950/40' },
  store_manager: { label: 'Store Manager', icon: Store, color: 'text-blue-400', bg: 'bg-blue-950/40' },
  stock_manager: { label: 'Stock Manager', icon: Package, color: 'text-emerald-400', bg: 'bg-emerald-950/40' },
  billing_executive: { label: 'Billing Exec', icon: CreditCard, color: 'text-amber-400', bg: 'bg-amber-950/40' },
  system: { label: 'System', icon: Bell, color: 'text-slate-400', bg: 'bg-slate-800/40' },
};

const actionColors = {
  'Created box': 'border-l-amber-500',
  'Resubmitted': 'border-l-blue-500',
  'APPROVED': 'border-l-emerald-500',
  'REJECTED': 'border-l-rose-500',
  'Bill': 'border-l-violet-500',
  'Stock': 'border-l-cyan-500',
  'merge': 'border-l-orange-500',
  'split': 'border-l-orange-500',
  'sale': 'border-l-emerald-500',
  'purchase': 'border-l-blue-500',
  'PO': 'border-l-indigo-500',
};

function getActionColor(action) {
  for (const [key, cls] of Object.entries(actionColors)) {
    if (action?.includes(key)) return cls;
  }
  return 'border-l-muted-foreground';
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [collapsedDates, setCollapsedDates] = useState({});

  const fetchNotifications = async () => {
    try {
      const params = { page, limit: 100 };
      if (roleFilter !== 'all') params.role = roleFilter;
      if (dateFilter) params.date = dateFilter;
      const res = await API.get('/notifications', { params });
      setNotifications(res.data.notifications); setTotal(res.data.total);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchNotifications(); }, [roleFilter, dateFilter, page]);

  // Group notifications by date
  const groupedByDate = useMemo(() => {
    const groups = {};
    for (const n of notifications) {
      const dateStr = n.created_at ? new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : 'Unknown';
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(n);
    }
    return groups;
  }, [notifications]);

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const roles = ['all', 'admin', 'store_manager', 'stock_manager', 'billing_executive', 'system'];

  return (
    <div data-testid="notifications-page">
      <div className="page-header">
        <h1>Activity Log</h1>
        <p>{total} total notifications</p>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-2 mb-4">
        <Input type="date" value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} className="w-full sm:w-48" data-testid="notifications-date-filter" />
        {dateFilter && <Button variant="ghost" size="sm" onClick={() => setDateFilter('')} className="text-xs">Clear</Button>}
      </div>

      {/* Role Tabs */}
      <Tabs value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
        <TabsList className="mb-4 w-full overflow-x-auto flex">
          {roles.map(r => {
            const cfg = roleConfig[r];
            const IconComp = cfg.icon;
            return (
              <TabsTrigger key={r} value={r} className="text-xs sm:text-sm gap-1.5 whitespace-nowrap" data-testid={`notif-tab-${r}`}>
                <IconComp size={14} className={cfg.color} />
                <span className="hidden sm:inline">{cfg.label}</span>
                <span className="sm:hidden">{r === 'all' ? 'All' : cfg.label.split(' ').map(w => w[0]).join('')}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Content for all tabs - same structure, filtered by role */}
        {roles.map(r => (
          <TabsContent key={r} value={r}>
            {Object.keys(groupedByDate).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground text-sm">
                  No notifications {r !== 'all' && `from ${roleConfig[r]?.label || r}`}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedByDate).map(([date, items]) => (
                  <Card key={date}>
                    <CardHeader className="pb-0 pt-3 px-3 sm:px-4 cursor-pointer" onClick={() => toggleDateCollapse(date)}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Clock size={14} className="text-muted-foreground" />
                          {date}
                          <span className="text-xs font-normal text-muted-foreground">({items.length})</span>
                        </CardTitle>
                        {collapsedDates[date] ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronUp size={16} className="text-muted-foreground" />}
                      </div>
                    </CardHeader>
                    {!collapsedDates[date] && (
                      <CardContent className="p-0">
                        <div className="divide-y divide-border/50">
                          {items.map(n => {
                            const cfg = roleConfig[n.role] || roleConfig.system;
                            const IconComp = cfg.icon;
                            const borderColor = getActionColor(n.action);
                            return (
                              <div key={n.id} className={`px-3 sm:px-4 py-2.5 flex items-start gap-2 sm:gap-3 hover:bg-muted/30 border-l-2 ${borderColor}`} data-testid="notification-item-reference-link">
                                <div className={`w-7 h-7 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                  <IconComp size={13} className={cfg.color} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs sm:text-sm leading-relaxed">{n.action}</p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                      <Clock size={9} />
                                      {n.created_at ? new Date(n.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                                    </span>
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>
                                      {(n.role || '').replace(/_/g, ' ')}
                                    </span>
                                    {n.user && n.user !== n.role && (
                                      <span className="text-[10px] text-muted-foreground">by {n.user}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {total > 100 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-xs text-muted-foreground">Page {page}/{Math.ceil(total / 100)}</span>
          <Button variant="outline" size="sm" disabled={page * 100 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
